export enum PlasticsDailyProductionBacklogSource {
    // Summed from open ordered products at the end of the day by the nightly rollup
    Snapshot = 'snapshot',
    // Back-calculated from ordered product createdOn / close dates when the table was seeded
    Reconstructed = 'reconstructed',
}

// One row per production day, written by cron_plastics_daily_production_rollup. The row id is
// the production date key (YYYY-MM-DD in America/Chicago).
export interface PlasticsDailyProduction {
    id: string;
    // Local midnight of the production day
    productionDate: Date;
    // The production day runs from 23:00 the previous calendar day to 23:00 (third shift
    // through second shift, per SELJAN_SHIFT_TIMES)
    dayStart: Date;
    dayEnd: Date;
    // Product instances created during the day with a GOOD outcome
    partsMolded: number;
    // Product instances created during the day with a BAD outcome
    partsRejected: number;
    // Quantities entered at the finishing stations during the day
    partsFinished: number;
    // Open (Draft / Released / Scheduled) ordered product quantity at the end of the day
    backlogParts: number | null;
    backlogSource: PlasticsDailyProductionBacklogSource | null;
    computedOn: Date;
}

export const PLASTICS_DAILY_PRODUCTION_TIME_ZONE = 'America/Chicago';
export const PLASTICS_PRODUCTION_DAY_END_HOUR = 23;

const DATE_KEY_PATTERN = /^(\d{4})-(\d{2})-(\d{2})$/;

// Minutes the time zone is ahead of UTC at the given instant (negative for America/Chicago)
function getTimeZoneOffsetMinutes(utcMs: number, timeZone: string): number {
    const parts = new Intl.DateTimeFormat('en-US', {
        timeZone,
        hourCycle: 'h23',
        year: 'numeric',
        month: '2-digit',
        day: '2-digit',
        hour: '2-digit',
        minute: '2-digit',
        second: '2-digit',
    }).formatToParts(new Date(utcMs));
    const value = (type: string) => Number(parts.find((part) => part.type === type)?.value);
    const localAsUtc = Date.UTC(value('year'), value('month') - 1, value('day'), value('hour'), value('minute'), value('second'));
    return Math.round((localAsUtc - Math.floor(utcMs / 1000) * 1000) / 60000);
}

// Converts a wall-clock time in the time zone to a UTC instant. Day boundaries here (00:00 and
// 23:00) never fall inside a DST gap, so the two-pass offset lookup is exact.
export function zonedTimeToUtc(
    year: number,
    monthIndex: number,
    day: number,
    hour: number,
    minute: number,
    timeZone: string = PLASTICS_DAILY_PRODUCTION_TIME_ZONE,
): Date {
    const wallClockAsUtc = Date.UTC(year, monthIndex, day, hour, minute, 0);
    const firstGuess = wallClockAsUtc - getTimeZoneOffsetMinutes(wallClockAsUtc, timeZone) * 60000;
    return new Date(wallClockAsUtc - getTimeZoneOffsetMinutes(firstGuess, timeZone) * 60000);
}

// The calendar date (YYYY-MM-DD) of the instant in the time zone
export function toZonedDateKey(date: Date, timeZone: string = PLASTICS_DAILY_PRODUCTION_TIME_ZONE): string {
    return new Intl.DateTimeFormat('en-CA', {
        timeZone,
        year: 'numeric',
        month: '2-digit',
        day: '2-digit',
    }).format(date);
}

export function addDaysToDateKey(dateKey: string, days: number): string {
    const match = DATE_KEY_PATTERN.exec(dateKey);
    if (!match) throw new Error(`invalid date key ${dateKey}`);
    const shifted = new Date(Date.UTC(Number(match[1]), Number(match[2]) - 1, Number(match[3]) + days));
    return shifted.toISOString().slice(0, 10);
}

export function getProductionDayWindow(dateKey: string): { productionDate: Date; dayStart: Date; dayEnd: Date } {
    const match = DATE_KEY_PATTERN.exec(dateKey);
    if (!match) throw new Error(`invalid date key ${dateKey}`);
    const year = Number(match[1]);
    const monthIndex = Number(match[2]) - 1;
    const day = Number(match[3]);
    return {
        productionDate: zonedTimeToUtc(year, monthIndex, day, 0, 0),
        dayStart: zonedTimeToUtc(year, monthIndex, day - 1, PLASTICS_PRODUCTION_DAY_END_HOUR, 0),
        dayEnd: zonedTimeToUtc(year, monthIndex, day, PLASTICS_PRODUCTION_DAY_END_HOUR, 0),
    };
}

// The most recent production day whose 23:00 end has passed
export function getLatestCompletedProductionDateKey(now: Date = new Date()): string {
    const todayKey = toZonedDateKey(now);
    return getProductionDayWindow(todayKey).dayEnd.getTime() <= now.getTime()
        ? todayKey
        : addDaysToDateKey(todayKey, -1);
}
